import { Injectable } from "@angular/core";
import { ModalController, Platform } from "@ionic/angular";
import { CameraPreview, CameraPreviewOptions, CameraPreviewPictureOptions } from '@starley/camera-preview-capacitor';
import { OptionPhotoModel } from "src/app/models/option-photo.model";
import { MmImagePreviewComponent } from "../../components/mm-image-preview/mm-image-preview.component";
import { MmLegiblePhotoComponent } from "../../components/mm-legible-photo/mm-legible-photo.component";


/**
 * Classe responsavel por manipulação de imagens
 * @author Starley Cazorla
 */

@Injectable()
export class ImageService {

    imageBase64Temp: any = null;
    cameraActive = false;
    torchActive = false;
    listUploadFile: Array<{ nome: string, img: any, imageFromGalery: boolean }> = []

    imgRotation = 0;

    constructor(private modalController: ModalController, private platform: Platform) { }

    /**
     * Checks camera availability
     * @author Starley Cazorla
     * @returns camera availability
     */
    async checkCameraAvailability(): Promise<boolean> {
        if (this.platform.is('ios') || this.platform.is('android')) {
            return true;
        } else {
            try {
                await CameraPreview.start({
                    position: 'rear'
                });
                // If no error, the camera is available
                console.log('Camera is available');
                CameraPreview.stop(); // Stop the camera if you just want to check availability
                return true;
            } catch (e) {
                CameraPreview.stop(); // Stop the camera if you just want to check availability
                console.log('Camera is not available', e);
                return false;
            }
        }
    }

    /**
     * Captura a imagem do preview
     */
    async touchTakePicture() {
        const cameraPreviewPictureOptions: CameraPreviewPictureOptions = {
            quality: 90,
            rotationFrontCamera: this.imgRotation
        };
        const result = await CameraPreview.capture(cameraPreviewPictureOptions);
        this.imageBase64Temp = result.value;
        this.stopCamera('photo');
    }

    /**
     * Abre o preview da camera centralizada (refatorar em caso de incompatibilidade com outros dispositivos)
     * @param positionCamera Se ira usar a camera da frente ou de trás
     */
    async cameraPreview(positionCamera?: string) {
        this.hideOrShowContentApp(['attach', 'main'], true);
        this.imgRotation = positionCamera === 'rear' ? 0 : 90;

        const cameraPreviewOptions: CameraPreviewOptions = {
            position: positionCamera ? positionCamera : 'rear',
            className: 'cameraPreview',
            parent: 'cameraPreview',
            enableZoom: false,
            enableHighResolution: true,
            disableAudio: true,
            rotateWhenOrientationChanged: true,
            enableOpacity: true,
            disableExifHeaderStripping: true,
            lockAndroidOrientation: true,
            toBack: true
        };

        CameraPreview.start(cameraPreviewOptions);
        this.cameraActive = true;
    }

    /**
     * Faz o flip da camera
     */
    touchFlipCamera() {
        CameraPreview.flip();
    }

    /**
     * Encerra o uso do preview
     * @param idModalDissmis - Id vinculated the modal when is close the front
     */
    async stopCamera(idModalDissmis: string) {
        this.hideOrShowContentApp(['attach', 'main'], false);


        await CameraPreview.stop();
        return await this.modalController.dismiss(this.imageBase64Temp, undefined, idModalDissmis);
    }

    /**
     * Touchs take photo
     * @author Starley Cazorla
     * @param optionPhoto
     * @returns take photo
     */
    async touchTakePhoto(optionPhoto: OptionPhotoModel): Promise<any> {
        return new Promise(async (resolve) => {
            this.imageBase64Temp = null;
            const modal = await this.modalController.create({
                id: 'photo',
                component: MmImagePreviewComponent,
                cssClass: 'transparent-modal modalInterno',
                backdropDismiss: optionPhoto.backdropDismiss,
                componentProps: { optionPhoto }
            });

            await modal.present();

            await modal.onDidDismiss().then(async (_data: any) => {
                this.stopCamera('photo');
                if (_data.data !== undefined) {

                    if (!optionPhoto.showPreviewImage) {
                        resolve(_data)
                    } else {
                        await this.checkPhoto(optionPhoto).then(res => {
                            if (res) {
                                resolve(_data)
                            }
                        });
                    }
                } else {
                    resolve(null);
                }
            });
        });
    }

    /**
     * Checks photo
     * @author Starley Cazorla
     * @param optionPhoto
     * @returns photo
     */
    async checkPhoto(optionPhoto: any): Promise<any> {
        return new Promise(async (resolve) => {
            const modal = await this.modalController.create({
                component: MmLegiblePhotoComponent,
                backdropDismiss: false,
                cssClass: 'modalInterno',
            });

            await modal.present();

            await modal.onDidDismiss().then(async (_data: any) => {
                if (_data.data === 'photoOk') {
                    resolve(_data.data);
                } else {
                    this.touchTakePhoto(optionPhoto);
                    resolve(null);
                }
            });
        });
    }

    /**
 * Hides or show content app
 * @author Starley Cazorla
 * @param listElements list of element id
 * @param isToHide boolean valeu
 */
    hideOrShowContentApp(listElements: any[], isToHide: boolean) {
        return listElements.forEach(item => {
            setTimeout(() => {
                document.getElementById(item)?.style.setProperty('visibility', isToHide ? 'hidden' : 'visible');
            }, 10);
        });
    }

    base64ToBlob(base64: string, contentType: string): Blob {
        // Decode Base64 string
        const byteCharacters = atob(base64);
        const byteArrays: any = [];

        // Split into chunks
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        // Create a Blob from the Byte Arrays
        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    }

}
